import React from 'react';
import { useTheme } from '@mui/material/styles';

// design
import Grid from '@mui/material/Grid';
import './ChildConfig.css';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import DoneIcon from '@mui/icons-material/Done';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import WarningIcon from '@mui/icons-material/Warning';

// マスタ値
import { useAtomValue } from 'jotai'
import {
  OptionsBase,
  OptionsSelectList,
  useReportTypeOptions,
  useProjectOptions,
  useGradeOptions,
  usePowerTrainOptions,
  useShimukeOptions,
  useLotOptions,
  useFreeOptions,
  initialFreeOptionState,
  attrTypeLabelMap,
  useApproveMemberList,
  ApproveMember,
  ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST,
} from '../../../state/master'
import { useMasterOptionsRefresher } from '../../../state/master'
import { useDivisionOptions, useTeamOptions, MAX_BODY_LENGTH } from '../../../state/master'
import {
  DIALOG_KEYS,
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';
import { filterOptionsByOrg } from '../../../lib/filters';
import { apiClient, isApiError } from '../../../lib/apiClient';
import { computeBodyType } from '../../common/computeBodyType';
import ConfirmDialog from '../../common/ConfirmDialog';
import '../ReadOnlyTable.css';

interface ConfigProps {
  division: number;
  team?: number;
  // 親（MasterManage）で /get_team_docset_list から取得した enlight_team を共有
  enlightTeam?: string | null;
  enlightSubteam?: string | null;
  // API送信用の内部パラメータ（UI表示には使用しない）
  enlightDocsetId?: number | null;
  teamDocsetData?: any | null;
  teamDocsetDataLoading?: boolean;
  teamDocsetDataError?: string | null;
  refreshTeamDocset?: () => Promise<void>;
  reporter?: string;
  setReporter?: (value: string) => void;
  startDate?: string;
  setStartDate?: (value: string) => void;
  endDate?: string;
  setEndDate?: (value: string) => void;
  reportType: string;
  setReportType: (value: string) => void;
  approver?: string;
  setApprover?: (value: string) => void;
  approverAM?: string;
  setApproverAM?: (value: string) => void;
  project?: string;
  setProject?: (value: string) => void;
  powerTrain?: string;
  setPowerTrain?: (value: string) => void;
  shimuke?: string;
  setShimuke?: (value: string) => void;
  lot?: string;
  setLot?: (value: string) => void;
  free1?: string;
  setFree1?: (value: string) => void;
  free2?: string;
  setFree2?: (value: string) => void;
  openSettingDialog: (
    title: string,
    meta: OptionsBase,
    rows: OptionsSelectList[],
    division?: boolean,
    freeOption?: boolean,
    editColor?: boolean,
    teamOverride?: number,
    dialogFilterByTeam?: boolean,
    enlightTeamArg?: string | null,
    teamDocsetColumnsArg?: any[],
    enlightSubteamArg?: string | null,
    enlightDocsetIdArg?: number | null,
  ) => void;
  openApproveDialog?: (rows: ApproveMember[]) => void;
  openEnlightColumnDialog?: () => void;
  openColumnInitializeDialog?: () => void;
  canEdit?: boolean;
}

const ENLIGHT_SYNC_STATUS_LABELS = {
  UNSYNCED: '同期なし',
  LINKED: 'Enlight作成のチームと連携',
  UNLINKED: '週報アプリがチームを作成済',
} as const;

function createSettingData (
  setting_id: number,
  meta: OptionsBase,
  name: string,
  type: string,
  required: boolean,
  visible: boolean,
  multiple_select?: boolean,
  editColor?: boolean,
  datas?: OptionsSelectList[]
) {
  // meta.options && delete meta.options;
  return { setting_id, meta, name, type, required, visible, multiple_select, editColor, datas };
}

const ChildConfig: React.FC<ConfigProps> = ({
  division, team, enlightTeam, enlightSubteam, enlightDocsetId,
  teamDocsetData, teamDocsetDataLoading, teamDocsetDataError, refreshTeamDocset,
  reporter, setReporter,
  startDate, setStartDate,
  endDate, setEndDate,
  reportType, setReportType,
  approver, setApprover,
  approverAM, setApproverAM,
  project, setProject,
  powerTrain, setPowerTrain,
  shimuke, setShimuke,
  lot, setLot,
  free1, setFree1,
  free2, setFree2,
  openSettingDialog,
  openApproveDialog,
  openEnlightColumnDialog,
  openColumnInitializeDialog,
  canEdit = true,
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  // マスタ値
  const { reportTypeOptions, reportTypeOptionsRaw } = useReportTypeOptions({ department_id: division, team_id: team });
  const reportTypeOptionsFiltered = reportTypeOptions;
  const { projectOptions, projectOptionsRaw } = useProjectOptions({ department_id: division, team_id: team });
  const { powerTrainOptions, powerTrainOptionsRaw } = usePowerTrainOptions({ department_id: division, team_id: team });
  const { shimukeOptions, shimukeOptionsRaw } = useShimukeOptions({ department_id: division, team_id: team });
  const { lotOptions, lotOptionsRaw } = useLotOptions({ department_id: division, team_id: team });
  const { gradeOptions, gradeOptionsRaw } = useGradeOptions({ department_id: division, team_id: team });
  const { freeOptions: freeOptionsFetched, freeOptionsRaw: freeOptionsRowFetched } = useFreeOptions({
    department_id: division,
    team_id: team,
  });
  const { refreshAllMasterOptions } = useMasterOptionsRefresher();
  const freeOptions = freeOptionsFetched ?? [];
  const freeOptionsRaw = freeOptionsRowFetched ?? [];

  // 本文最大文字数（課/チーム）
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const getNumberOrUndefined = (raw: any): number | undefined => {
    const v = Number(raw);
    return Number.isFinite(v) && v > 0 ? v : undefined;
  };

  const divisionBodyMax = React.useMemo(() => {
    const found = (divisionOptions ?? []).find(
      (d: any) => Number((d as any)?.department_id) === Number(division)
    );
    return getNumberOrUndefined((found as any)?.body_max_length);
  }, [divisionOptions, division]);

  const teamBodyMax = React.useMemo(() => {
    const found = (teamOptions ?? []).find(
      (t: any) => Number((t as any)?.team_id) === Number(team)
    );
    return getNumberOrUndefined((found as any)?.body_max_length);
  }, [teamOptions, team]);

  // 本文最大文字数（実効値）と設定箇所
  const effectiveBodyMax = (typeof teamBodyMax === 'number'
    ? teamBodyMax
    : (typeof divisionBodyMax === 'number'
      ? divisionBodyMax
      : MAX_BODY_LENGTH));
  const effectiveSource = (typeof teamBodyMax === 'number'
    ? 'チーム'
    : (typeof divisionBodyMax === 'number'
      ? '課'
      : 'Default'));

  // Enlight 連携状態（親から共有された enlight_team で判定）
  const normalizeEnlightTeam = (v: any): string | null => {
    const s = String(v ?? '').trim();
    if (!s) return null;
    const lc = s.toLowerCase();
    if (lc === 'none' || lc === 'null') return null;
    return s;
  };
  const sharedEnlightTeam = normalizeEnlightTeam(enlightTeam);
  const sharedEnlightSubteam = normalizeEnlightTeam(enlightSubteam);
  // API送信用の内部パラメータ（UI表示には使用しない）
  const sharedEnlightDocsetId = (typeof enlightDocsetId === 'number' && Number.isFinite(enlightDocsetId))
    ? enlightDocsetId
    : null;

  const [teamDocsetCount, setTeamDocsetCount] = React.useState<number | null>(null);
  const [teamDocsetLoading, setTeamDocsetLoading] = React.useState<boolean>(false);
  const [teamDocsetError, setTeamDocsetError] = React.useState<string | null>(null);

  const [fieldCount, setFieldCount] = React.useState<number | null>(null);
  const [fieldLoading, setFieldLoading] = React.useState<boolean>(false);
  const [fieldError, setFieldError] = React.useState<string | null>(null);
  const [creatingDocset, setCreatingDocset] = React.useState<boolean>(false);
  const [deletingDocset, setDeletingDocset] = React.useState<boolean>(false);
  const [docsetIdMissing, setDocsetIdMissing] = React.useState<boolean>(false);
  const [currentDocsetId, setCurrentDocsetId] = React.useState<number | null>(null);
  const [resolvedEnlightTeam, setResolvedEnlightTeam] = React.useState<string | null>(null);
  const [createDocsetErrorDialog, setCreateDocsetErrorDialog] = React.useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const enlightLinkedByResponse = Boolean(resolvedEnlightTeam);

  const syncedWithEnlightLink = React.useMemo(() => {
    return enlightLinkedByResponse && !teamDocsetLoading && !fieldLoading && !teamDocsetError && !fieldError && !docsetIdMissing;
  }, [enlightLinkedByResponse, teamDocsetLoading, fieldLoading, teamDocsetError, fieldError, docsetIdMissing]);

  const enlightSyncStatusLabel = React.useMemo(() => {
    if (teamDocsetLoading || fieldLoading) return ENLIGHT_SYNC_STATUS_LABELS.UNSYNCED;
    if (teamDocsetError || fieldError) return ENLIGHT_SYNC_STATUS_LABELS.UNSYNCED;
    if (docsetIdMissing) return ENLIGHT_SYNC_STATUS_LABELS.UNSYNCED;
    return syncedWithEnlightLink ? ENLIGHT_SYNC_STATUS_LABELS.LINKED : ENLIGHT_SYNC_STATUS_LABELS.UNLINKED;
  }, [teamDocsetLoading, fieldLoading, teamDocsetError, fieldError, docsetIdMissing, syncedWithEnlightLink]);

  const enlightSyncStatusColor = React.useMemo(() => {
    if (enlightSyncStatusLabel === ENLIGHT_SYNC_STATUS_LABELS.UNSYNCED) return isDark ? '#f87171' : '#d32f2f';
    if (enlightSyncStatusLabel === ENLIGHT_SYNC_STATUS_LABELS.UNLINKED) return isDark ? '#38bdf8' : '#1976d2';
    return isDark ? '#4ade80' : '#2e7d32';
  }, [enlightSyncStatusLabel, isDark]);
  const isStatusLoading = teamDocsetLoading || fieldLoading || creatingDocset || deletingDocset;
  const isUnsyncedStatus = enlightSyncStatusLabel === ENLIGHT_SYNC_STATUS_LABELS.UNSYNCED;
  const isSyncedWithoutLinkStatus = enlightSyncStatusLabel === ENLIGHT_SYNC_STATUS_LABELS.UNLINKED;

  const handleCreateDocset = React.useCallback(async () => {
    setCreatingDocset(true);
    try {
      await apiClient.post('/create_docset', {
        department_id: Number(division) || 0,
        team_id: Number(team) || 0,
        update_flg: false,
      });
      if (refreshTeamDocset) await refreshTeamDocset();
    } catch (e) {
      console.error('create_docset post error', e);
      const defaultMessage = 'Enlight側の連携先の新規作成に失敗しました。システム管理者にご確認ください。';
      let message = defaultMessage;
      if (isApiError(e)) {
        const responseMessage = String(
          (e as any)?.response?.data?.message ??
          (e as any)?.response?.data?.error ??
          ''
        ).trim();
        if (responseMessage) {
          message = `${defaultMessage}\n\n詳細: ${responseMessage}`;
        }
      }
      setCreateDocsetErrorDialog({ open: true, message });
    } finally {
      setCreatingDocset(false);
    }
  }, [division, team, refreshTeamDocset]);

  const handleDeleteDocset = React.useCallback(async () => {
    const docsetId = Number(currentDocsetId);
    if (!Number.isFinite(docsetId)) return;
    setDeletingDocset(true);
    try {
      await apiClient.post('/delete_docset', {
        department_id: Number(division) || 0,
        team_id: Number(team) || 0,
        docset_id: docsetId,
      });
      if (refreshTeamDocset) await refreshTeamDocset();
    } catch (e) {
      console.error('delete_docset post error', e);
    } finally {
      setDeletingDocset(false);
    }
  }, [division, team, currentDocsetId, refreshTeamDocset]);

  // Enlight連携: /get_team_docset_list の columns（attr_id と enlight_column_name など）
  const [teamDocsetColumns, setTeamDocsetColumns] = React.useState<any[]>([]);
 
  // フリー項目 削除確認ダイアログ状態
  const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; attrId: number | null; targetName: string; departmentId: number | null; teamId: number | null; processing: boolean; usingList: any[] }>({
    open: false,
    attrId: null,
    targetName: '',
    departmentId: null,
    teamId: null,
    processing: false,
    usingList: [],
  });

  React.useEffect(() => {
    let cancelled = false;
    const extractEnlightTeamFromDocset = (data: any): string | null => {
      const arr = Array.isArray(data)
        ? data
        : (Array.isArray((data as any)?.list) ? (data as any).list
          : Array.isArray((data as any)?.data) ? (data as any).data
          : []);
      if (!Array.isArray(arr) || arr.length === 0) {
        return normalizeEnlightTeam((data as any)?.enlight_team ?? (data as any)?.enlightTeam ?? null);
      }
      const picked = arr.find((x: any) => String((x as any)?.enlight_team ?? (x as any)?.enlightTeam ?? '').trim() !== '') ?? arr[0];
      return normalizeEnlightTeam((picked as any)?.enlight_team ?? (picked as any)?.enlightTeam ?? null);
    };

    const run = async () => {
      if (teamDocsetDataLoading) {
        setTeamDocsetLoading(true);
        setFieldLoading(true);
        setTeamDocsetError(null);
        setFieldError(null);
        return;
      }
      if (teamDocsetDataError) {
        setResolvedEnlightTeam(null);
        setTeamDocsetError('取得に失敗しました');
        setFieldError(null);
        setTeamDocsetCount(null);
        setFieldCount(null);
        setDocsetIdMissing(false);
        setCurrentDocsetId(null);
        setTeamDocsetLoading(false);
        setFieldLoading(false);
        return;
      }
      setTeamDocsetLoading(false);
      setTeamDocsetError(null);
      setFieldLoading(true);
      setFieldError(null);

      // list 件数（/get_team_docset_list の columns 件数を採用）
      const data: any = teamDocsetData;
      const fetchedEnlightTeam = extractEnlightTeamFromDocset(data);
      const isLinked = Boolean(fetchedEnlightTeam);
      const responseDocsetId = (data as any)?.docset_id;
      const isDocsetIdMissing = responseDocsetId === null || responseDocsetId === undefined || String(responseDocsetId).trim() === '';
      const parsedDocsetId = Number(responseDocsetId);
      const normalizedDocsetId = Number.isFinite(parsedDocsetId) ? parsedDocsetId : null;
      let listCount = 0;
      const cols: any = data?.columns;
      if (Array.isArray(cols)) {
        listCount = cols.length;
      } else if (cols && typeof cols === 'object') {
        // オブジェクト形式でもキー数を件数扱い
        listCount = Object.keys(cols).length;
      } else if (Number.isFinite(Number(cols))) {
        // 数値で返るケースも許容
        listCount = Number(cols);
      }
      // カラム配列に正規化（ChildConfig 全体で共有）
      const colsArray: any[] = Array.isArray(cols)
        ? cols
        : (cols && typeof cols === 'object')
          ? Object.values(cols)
          : [];

      // Field 件数（enlight_team がある場合のみ取得）
      let fields: any[] = [];
      if (isLinked) {
        try {
          const resDetail = await apiClient.get('/get_docset_id_detail', {
            params: {
              enlight_team: String(fetchedEnlightTeam ?? ''),
            },
          });
          const fetchedFields = (resDetail as any)?.data?.json_data?.schema?.fields;
          fields = Array.isArray(fetchedFields) ? fetchedFields : [];
        } catch {
          if (!cancelled) {
            setFieldError('取得に失敗しました');
          }
        }
      }
      const fcount = fields.length;

      // direct_columns に基づく「自動紐付け」カウントを加算（存在しない場合は ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST を後方互換として使用）
      let absoluteMatchCount = 0;
      if (isLinked && Array.isArray(fields) && fields.length > 0) {
        // /get_team_docset_list の direct_columns を優先
        const dcolsArray: any[] = Array.isArray((data as any)?.direct_columns)
          ? (data as any).direct_columns
          : ((data as any)?.direct_columns && typeof (data as any).direct_columns === 'object'
              ? Object.values((data as any).direct_columns)
              : []);
        const absoluteKeys = new Set<string>();

        if (Array.isArray(dcolsArray) && dcolsArray.length > 0) {
          // direct_columns: ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST の Key= enlight_column_name, Value= column_name
          dcolsArray.forEach((rec: any) => {
            const k = String(rec?.enlight_column_name ?? '').trim();
            if (k) absoluteKeys.add(k);
          });
        } else {
          // 後方互換: 定数に基づくキー
          (ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST ?? []).forEach((obj: any) => {
            const keys = Object.keys(obj ?? {});
            if (keys.length > 0) absoluteKeys.add(String(keys[0]));
          });
        }

        absoluteMatchCount = fields.reduce((acc: number, f: any) => {
          const fname = String(f?.name ?? '');
          return acc + (absoluteKeys.has(fname) ? 1 : 0);
        }, 0);
      }

      if (!cancelled) {
        setResolvedEnlightTeam(fetchedEnlightTeam);
        setTeamDocsetColumns(colsArray);
        setTeamDocsetCount(listCount + absoluteMatchCount);
        setFieldCount(isLinked ? fcount : null);
        setDocsetIdMissing(isDocsetIdMissing);
        setCurrentDocsetId(normalizedDocsetId);
        if (!isLinked) setFieldError(null);
        setFieldLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [teamDocsetData, teamDocsetDataLoading, teamDocsetDataError]);

  // フリー項目初期値
  const initialFreeOption = useAtomValue(initialFreeOptionState);
  initialFreeOption.department_id = division;

  // multiple_selectを0/1/booleanからbooleanに正規化
  const normalizeMultiple = (v: any): boolean => Number(v) === 1;
  // 項目表示フラグ: visible を優先し、未設定時のみ status を後方互換で参照
  const normalizeVisible = (visible: any, status: any): boolean => {
    if (visible != null) return typeof visible === 'string' ? visible === 'Y' : Boolean(visible);
    return typeof status === 'string' ? status === 'Y' : Boolean(status);
  };
  const hasColorOptions = (options?: OptionsSelectList[]): boolean => {
    if (!Array.isArray(options)) return false;
    return options.some((opt: any) => String(opt?.color ?? '').trim().length > 0);
  };
  const resolveEditColor = (meta: any): boolean => {
    return Boolean((meta as any)?.set_color) || hasColorOptions((meta as any)?.options);
  };
  const toOrgId = (v: any): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const pickPreferredMeta = (candidates?: OptionsBase[]): OptionsBase | undefined => {
    const list = (Array.isArray(candidates) ? candidates : []).filter(Boolean) as OptionsBase[];
    if (list.length === 0) return undefined;

    const depId = toOrgId(division);
    const teamId = toOrgId(team);
    const score = (item: any): number => {
      const did = toOrgId((item as any)?.department_id);
      const tid = toOrgId((item as any)?.team_id);
      let s = 0;
      if (depId > 0 && teamId > 0) {
        if (did === depId && tid === teamId) s = 5000;
        else if (did === depId && tid === 0) s = 4000;
        else if (did === 0 && tid === 0) s = 3000;
        else if (did === depId) s = 2000;
        else if (did === 0) s = 1000;
      } else if (depId > 0) {
        if (did === depId && tid === 0) s = 4500;
        else if (did === depId) s = 3500;
        else if (did === 0 && tid === 0) s = 2500;
        else if (did === 0) s = 1500;
      } else {
        if (did === 0 && tid === 0) s = 2000;
        else if (did === 0) s = 1000;
        else s = 500;
      }
      if (String((item as any)?.status ?? '').toUpperCase() === 'Y') s += 100;
      s += Math.min(99, Number((item as any)?.options?.length ?? 0));
      return s;
    };

    const picked = [...list].sort((a, b) => score(b) - score(a))[0];
    const name = String((picked as any)?.attr_name ?? picked?.label ?? '').trim();
    const optionsLen = Array.isArray((picked as any)?.options) ? (picked as any).options.length : 0;
    const typeNum = Number((picked as any)?.attr_type);
    const isSelectLike = [11, 12, 13].includes(typeNum);
    const isCommonFixedName = [CLASSIFICATION_MARKET_TARGET_ATTR_NAME, FIXED_ATTR_NAME_PROJECT, FIXED_ATTR_NAME_POWERTRAIN, 'グレード', FIXED_ATTR_NAME_SHIMUKE, FIXED_ATTR_NAME_LOT].includes(name);
    if (isCommonFixedName && optionsLen > 0 && !isSelectLike) {
      return { ...(picked as any), attr_type: 11 } as OptionsBase;
    }
    return picked;
  };

  // 並び順（order_number）取得ユーティリティ
  // metaRaw -> meta -> 自身 の順で order_number を探索し、未定義は末尾へ
  const extractOrder = (v: any): number => {
    const n = Number(v?.order_number ?? v?.orderNumber);
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
  };
  const compareByOrderNumber = (a: any, b: any): number => {
    const oa = extractOrder(a?.metaRaw ?? a?.meta ?? a);
    const ob = extractOrder(b?.metaRaw ?? b?.meta ?? b);
    return oa - ob;
  };

  // options フィルタ（division / team）: 共通関数 filterOptionsByOrg を使用

  // 共通項目
  // 共通項目は status='N' でも行を表示するため、Raw（未サニタイズ）メタを採用する
  const reportTypeMeta = pickPreferredMeta(reportTypeOptionsRaw);
  const projectMeta = pickPreferredMeta(projectOptionsRaw);
  const powerTrainMeta = pickPreferredMeta(powerTrainOptionsRaw);
  const gradeMeta = pickPreferredMeta(gradeOptionsRaw);
  const shimukeMeta = pickPreferredMeta(shimukeOptionsRaw);
  const lotMeta = pickPreferredMeta(lotOptionsRaw);

  let commonRows = [
    reportTypeMeta
      ? createSettingData(
          reportTypeMeta.attr_id,
          reportTypeMeta,
          reportTypeMeta.label,
          reportTypeMeta.type ?? '',
          reportTypeMeta.required,
          normalizeVisible((reportTypeMeta as any).visible, reportTypeMeta.status),
          normalizeMultiple(reportTypeMeta.multiple_select),
          true, // 週報分類は常に色・他課公開列を表示
          reportTypeMeta.options
        )
      : null,
    projectMeta
      ? createSettingData(
          projectMeta.attr_id,
          projectMeta,
          projectMeta.label,
          projectMeta.type ?? '',
          projectMeta.required,
          normalizeVisible((projectMeta as any).visible, projectMeta.status),
          normalizeMultiple(projectMeta.multiple_select),
          resolveEditColor(projectMeta),
          projectMeta.options
        )
      : null,
    powerTrainMeta
      ? createSettingData(
          powerTrainMeta.attr_id,
          powerTrainMeta,
          powerTrainMeta.label,
          powerTrainMeta.type ?? '',
          powerTrainMeta.required,
          normalizeVisible((powerTrainMeta as any).visible, powerTrainMeta.status),
          normalizeMultiple(powerTrainMeta.multiple_select),
          resolveEditColor(powerTrainMeta),
          powerTrainMeta.options
        )
      : null,
    gradeMeta
      ? createSettingData(
          gradeMeta.attr_id,
          gradeMeta,
          gradeMeta.label,
          gradeMeta.type ?? '',
          gradeMeta.required,
          normalizeVisible((gradeMeta as any).visible, gradeMeta.status),
          normalizeMultiple(gradeMeta.multiple_select),
          resolveEditColor(gradeMeta),
          gradeMeta.options
        )
      : null,
    shimukeMeta
      ? createSettingData(
          shimukeMeta.attr_id,
          shimukeMeta,
          shimukeMeta.label,
          shimukeMeta.type ?? '',
          shimukeMeta.required,
          normalizeVisible((shimukeMeta as any).visible, shimukeMeta.status),
          normalizeMultiple(shimukeMeta.multiple_select),
          resolveEditColor(shimukeMeta),
          shimukeMeta.options
        )
      : null,
    lotMeta
      ? createSettingData(
          lotMeta.attr_id,
          lotMeta,
          lotMeta.label,
          lotMeta.type ?? '',
          lotMeta.required,
          normalizeVisible((lotMeta as any).visible, lotMeta.status),
          normalizeMultiple(lotMeta.multiple_select),
          resolveEditColor(lotMeta),
          lotMeta.options
        )
      : null,
  ].filter(Boolean) as any[];

  // ダイアログで status='N' も表示できるよう、raw（未サニタイズ）な meta を紐づける
  const metaRawMap: Record<number, OptionsBase | undefined> = {};
  if (reportTypeMeta) metaRawMap[reportTypeMeta.attr_id] = reportTypeMeta;
  if (projectMeta) metaRawMap[projectMeta.attr_id] = projectMeta;
  if (powerTrainMeta) metaRawMap[powerTrainMeta.attr_id] = powerTrainMeta;
  if (gradeMeta) metaRawMap[gradeMeta.attr_id] = gradeMeta;
  if (shimukeMeta) metaRawMap[shimukeMeta.attr_id] = shimukeMeta;
  if (lotMeta) metaRawMap[lotMeta.attr_id] = lotMeta;

  commonRows = commonRows.map((row: any) => ({
    ...row,
    metaRaw: metaRawMap[row.setting_id] ?? row.meta
  }));
  // 共通項目の行を order_number 順にソート
  commonRows.sort(compareByOrderNumber);

  // フリー項目
  const freeRows = freeOptions.map((freeRow, index) => {
    const inputRequiredRaw = (freeRow as any)?.input_required;
    const inputRequired =
      typeof inputRequiredRaw === 'boolean'
        ? inputRequiredRaw
        : Number(inputRequiredRaw ?? 0) === 1;
    return createSettingData(
      freeRow.attr_id,
      freeRow,
      freeRow.label,
      freeRow.type ?? '',
      typeof inputRequiredRaw === 'undefined' ? freeRow.required : inputRequired,
      normalizeVisible((freeRow as any).visible, freeRow.status),
      normalizeMultiple(freeRow.multiple_select),
      resolveEditColor(freeRow),
      freeRow.options
    )
  });
  
  // フリー項目 raw meta 紐づけ（status='N' もダイアログで表示できるようにする）
  const freeRawMap: Record<number, OptionsBase | undefined> = {};
  if (Array.isArray(freeOptionsRaw)) {
    freeOptionsRaw.forEach((it: OptionsBase) => {
      freeRawMap[it.attr_id] = it;
    });
  }
  const freeRowsRawAttached = freeRows.map((row: any) => ({
    ...row,
    metaRaw: freeRawMap[row.setting_id] ?? row.meta
  }));
  // フリー項目の行を order_number 順にソート
  freeRowsRawAttached.sort(compareByOrderNumber);

  // 承認者一覧（approveMemberListState）取得とフィルタ
  const { approveMemberList, isLoading: isApproveLoading } = useApproveMemberList();

  const uniqueNames = (arr: string[]) => Array.from(new Set(arr));

  const filteredApproveMembers = React.useMemo(() => {
    const divId = Number(division);
    // 承認者はチームでのフィルタ不要（課のみでフィルタ）
    return (approveMemberList ?? [])
      .filter((m: any) => Number(m?.department_id) === divId);
  }, [approveMemberList, division]);

  const approve3Names = React.useMemo(() => {
    const list = (filteredApproveMembers ?? [])
      .filter((m: any) => Number((m as any)?.approve_3 ?? (m as any)?.approve3) === 1)
      .map((m: any) => String((m as any)?.name ?? '').trim())
      .filter(Boolean);
    return uniqueNames(list);
  }, [filteredApproveMembers]);

  const approveAmNames = React.useMemo(() => {
    const list = (filteredApproveMembers ?? [])
      .filter((m: any) => Number((m as any)?.approve_am ?? (m as any)?.approveAm) === 1)
      .map((m: any) => String((m as any)?.name ?? '').trim())
      .filter(Boolean);
    return uniqueNames(list);
  }, [filteredApproveMembers]);
  
  // フリー項目の削除確認を開く
  const handleDeleteFreeAttribute = React.useCallback(async (attrIdRaw?: number, name?: string) => {
    const attrId = Number(attrIdRaw);
    if (!Number.isFinite(attrId)) return;
    try {
      const resp = await apiClient.get('/check_attribute_using', { params: { attr_id: attrId } });
      const list = Array.isArray(resp?.data) ? resp.data : [];
      setDeleteDialog({
        open: true,
        attrId,
        targetName: String(name ?? ''),
        departmentId: Number(division) || 0,
        teamId: Number(team) || 0,
        processing: false,
        usingList: list,
      });
    } catch (e) {
      console.error('check_attribute_using get error', e);
      setDeleteDialog({
        open: true,
        attrId,
        targetName: String(name ?? ''),
        departmentId: Number(division) || 0,
        teamId: Number(team) || 0,
        processing: false,
        usingList: [],
      });
    }
  }, [division, team]);

  // 削除ダイアログ: 確定処理
  const confirmDeleteFreeAttribute = React.useCallback(async () => {
    const attrId = Number(deleteDialog.attrId);
    if (!Number.isFinite(attrId)) {
      setDeleteDialog({ open: false, attrId: null, targetName: '', departmentId: null, teamId: null, processing: false, usingList: [] });
      return;
    }
    setDeleteDialog(prev => ({ ...prev, processing: true }));
    try {
      await apiClient.post('/delete_free_attribute', {
        attr_id: attrId,
        department_id: deleteDialog.departmentId ?? (Number(division) || 0),
        team_id: deleteDialog.teamId ?? (Number(team) || 0),
        attr_name: deleteDialog.targetName,
      });
      console.info('delete_free_attribute success', attrId);
      // 削除成功後に最新状態を再取得して再レンダリング（全体）
      await refreshAllMasterOptions();
    } catch (e) {
      console.error('delete_free_attribute post error', e);
    } finally {
      setDeleteDialog({ open: false, attrId: null, targetName: '', departmentId: null, teamId: null, processing: false, usingList: [] });
    }
  }, [deleteDialog.attrId, deleteDialog.departmentId, deleteDialog.teamId, deleteDialog.targetName, division, team, refreshAllMasterOptions]);

  // 削除ダイアログ: 閉じる
  const closeDeleteDialog = React.useCallback(() => {
    if (deleteDialog.processing) return;
    setDeleteDialog({ open: false, attrId: null, targetName: '', departmentId: null, teamId: null, processing: false, usingList: [] });
  }, [deleteDialog.processing]);
  
  return (
  <FormControl fullWidth sx={{ px: 4, my: 2 }}>
    <Card sx={{ backgroundColor: isDark ? '#1e293b' : '#f0f0f0', mb: 2, display: 'none' }}>
      <CardContent>
        <Grid container spacing={2} className="search-row">
          {/* Enlight連携 */}
          <Grid size={4} className="search-item date-item" display="flex">
            <h4>・Enlight連携</h4>
          </Grid>
          <Grid size={5} className="search-item date-item" display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start">
            <p className="setting-value" style={{ margin: 0 }}>
              <span style={{ color: enlightSyncStatusColor }}>Enlight連携状態: {enlightSyncStatusLabel}</span>
            </p>
            {syncedWithEnlightLink && (
              <p className="setting-value" style={{ margin: 0 }}>
                {teamDocsetLoading || fieldLoading
                  ? '設定済みのEnlightカラム: 読込中...'
                  : (teamDocsetError || fieldError)
                    ? '設定済みのEnlightカラム: 取得に失敗しました'
                    : `設定済みのEnlightカラム: ${teamDocsetCount ?? 0} / ${fieldCount ?? 0} 件`}
              </p>
            )}
          </Grid>
          <Grid size={3} className="search-item date-item" display="flex">
            {!isStatusLoading && isUnsyncedStatus && (
              <Button size="small" variant="contained" color="primary" onClick={handleCreateDocset}>
                Enlight側の連携先を新規作成
              </Button>
            )}
            {!isStatusLoading && isSyncedWithoutLinkStatus && (
              <Button size="small" variant="contained" color="error" onClick={handleDeleteDocset} disabled={!Number.isFinite(Number(currentDocsetId))}>
                Enlight側の連携先をを削除
              </Button>
            )}
            {!isStatusLoading && syncedWithEnlightLink && (
              <Button size="small" variant="contained" color="success" onClick={() => openEnlightColumnDialog && openEnlightColumnDialog()}>
                Enlightカラムを設定する
              </Button>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    <Card sx={{ backgroundColor: isDark ? '#1e293b' : '#f0f0f0' }}>
      <CardContent>
        <Grid container spacing={2} className="search-row">
          {/* 承認者 */}
          <Grid size={2} className="search-item date-item" display="flex">
            <h4>・承認者</h4>
          </Grid>
          <Grid size={12} className="search-item date-item" display="flex" justifyContent="flex-start" alignItems="flex-start">
            <TableContainer component={Paper} className={canEdit ? undefined : 'read-only-table'}>
              <Table
                stickyHeader
                sx={{ minWidth: 650 }}
                aria-label="simple table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell className="Appove-3">職制</TableCell>
                    <TableCell className="Approve-AM">AM</TableCell>
                    <TableCell className="SettingButton">変更</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top', textAlign: 'left' }}>
                      <div className="settings-scroll" style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 8 }}>
                        {/* 承認-3: approve_3=1 の一覧（department でフィルタ、team は team_id=0 を全体扱い） */}
                        {isApproveLoading ? (
                          <p className="setting-value">読込中...</p>
                        ) : approve3Names.length > 0 ? (
                          approve3Names.map((name) => (
                            <p className="setting-value" key={name}>{name}</p>
                          ))
                        ) : (
                          <p className="setting-value">—</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', textAlign: 'left' }}>
                      <div className="settings-scroll" style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 8 }}>
                        {/* 承認-AM: approve_am=1 の一覧（department でフィルタ、team は team_id=0 を全体扱い） */}
                        {isApproveLoading ? (
                          <p className="setting-value">読込中...</p>
                        ) : approveAmNames.length > 0 ? (
                          approveAmNames.map((name) => (
                            <p className="setting-value" key={name}>{name}</p>
                          ))
                        ) : (
                          <p className="setting-value">—</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell component="td" scope="row">
                      {openApproveDialog && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => openApproveDialog(filteredApproveMembers as unknown as ApproveMember[])}
                        >
                          設定変更
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {/* 承認者 */}
          {/* 本文最大文字数 */}
          <Grid size={12} className="search-item date-item" display="flex" alignItems="left">
            <p className="setting-value" style={{ margin: 0, whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 600 }}>・本文最大文字数：</span>
              <b style={{ marginLeft: 4 }}>{effectiveBodyMax}</b>
              <span style={{ fontSize: '0.85em', marginLeft: 4 }}>
                （{effectiveSource}設定値）
              </span>
            </p>
          </Grid>
          {/* 本文最大文字数 */}
          {/* 基本項目 */}
          <Grid size={2} className="search-item date-item" display="flex">
            <h4>・共通項目</h4>
          </Grid>
          <Grid size={2} offset={8}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => openColumnInitializeDialog && openColumnInitializeDialog()}
              disabled={!canEdit}
            >全項目を再設定</Button>
          </Grid>
          <Grid size={12} className="search-item date-item" display="flex" justifyContent="center" alignItems="center">
            <TableContainer component={Paper} className={canEdit ? undefined : 'read-only-table'}>
              <Table
                stickyHeader
                sx={{ minWidth: 650 }}
                aria-label="simple table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell className="ColumnName">項目</TableCell>
                    <TableCell className="InputType">入力方法</TableCell>
                    <TableCell className="CheckColumn">項目表示</TableCell>
                    <TableCell className="CheckColumn">複数選択</TableCell>
                    <TableCell className="CheckColumn">並替キー</TableCell>
                    <TableCell>設定値</TableCell>
                    <TableCell>Enlightカラム名</TableCell>
                    <TableCell className="SettingButton">変更</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commonRows.map((row) => (
                    <TableRow
                      key={row.name}
                    >
                      <TableCell component="td" scope="row">
                        {/* 項目名 */}
                        <strong>{row.name}</strong>
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 入力方法 */}
                        {mapAttrTypeToLabel(row.meta?.attr_type, row.type)}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 項目表示（division/team フィルタ後に1件以上で表示） */}
                        {row.visible && (
                          <DoneIcon color="primary"></DoneIcon>
                        )}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 複数選択 */}
                        {row.multiple_select && (
                          <DoneIcon color="primary"></DoneIcon>
                        )}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 並替キー */}
                        {(() => {
                          const v = row.metaRaw?.use_for_sort ?? (row.meta as any)?.use_for_sort;
                          const enabled = v === true || v === 1 || String(v).toLowerCase() === 'true';
                          return enabled ? <DoneIcon color="primary" /> : null;
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="settings-scroll" style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 8 }}>
                          {/* 設定値（division/team でフィルタ、status='N' は非表示） */}
                          {filterOptionsByOrg(row.datas, division, team)
                            .filter((line: OptionsSelectList) => String((line as any).status ?? 'Y').toUpperCase() !== 'N')
                            .map((line: OptionsSelectList) => {
                              const color = line.color;
                              const displayValue = String((line as any).value ?? (line as any).label ?? '').trim();
                              const rowKey = String((line as any).value ?? (line as any).label ?? (line as any).option_id ?? '');
                              if (color) {
                                return (
                                  <p className="setting-value" key={rowKey}>
                                    {displayValue}
                                    <input type="color" className="color-setting" value={color} disabled></input>
                                  </p>
                                )
                              } else {
                                return (
                                  <p className="setting-value" key={rowKey}>
                                    {displayValue}
                                  </p>
                                )
                              }
                            })}
                        </div>
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* Enlightカラム名 */}
                        {(() => {
                          const id = Number(row?.setting_id);
                          const c = (teamDocsetColumns ?? []).find((x: any) => Number(x?.attr_id) === id);
                          const name = String(c?.enlight_column_name ?? '').trim();
                          const dtypeRaw = String((c as any)?.enlight_dype ?? (c as any)?.enlight_dtype ?? '').trim();
                          const dtype = normalizeEnlightDtype(dtypeRaw);
                          const expected = computeBodyType(row.meta?.attr_type, row.multiple_select);
                          const mismatch = Boolean(dtype) && Boolean(expected) && dtype !== expected;
                          if (!name) return (<span className="setting-value">—</span>);
                          return (
                            <span className="setting-value">
                              {name}
                              <br />
                              <span style={{ fontSize: '0.75em' }}>
                                {dtype ? `[${dtype}]` : ''}
                                {mismatch && (
                                  <Tooltip title="データ種別が異なるため、変換されてEnlightに登録されます">
                                    <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                  </Tooltip>
                                )}
                              </span>
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 変更ボタン（SettingDialog では raw meta を使って status='N' も表示） */}
                        <Button variant="contained" color="success" onClick={() => openSettingDialog(row.name, row.metaRaw, filterOptionsByOrg(row.datas, division, team), false, false, row.editColor, team, true, sharedEnlightTeam, teamDocsetColumns, sharedEnlightSubteam, sharedEnlightDocsetId)}>設定変更</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {/* フリー項目 */}
          <Grid size={2} className="search-item date-item">
            <h4>・フリー項目</h4>
          </Grid>
          <Grid size={2} offset={8}>
            <Button variant="contained" color="secondary" onClick={() => openSettingDialog(DIALOG_KEYS.NEW_FREE_ITEM, initialFreeOption, [], false, true, false, undefined, undefined, sharedEnlightTeam, teamDocsetColumns, sharedEnlightSubteam, sharedEnlightDocsetId)} disabled={!canEdit}>新規フリー項目</Button>
          </Grid>
          <Grid size={12} className="search-item date-item" display="flex" justifyContent="center" alignItems="center">
            <TableContainer component={Paper} className={canEdit ? undefined : 'read-only-table'}>
              <Table
                stickyHeader
                sx={{ minWidth: 650 }}
                aria-label="simple table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell className="ColumnName">項目</TableCell>
                    <TableCell className="InputType">入力方法</TableCell>
                    <TableCell className="CheckColumn">必須</TableCell>
                    <TableCell className="CheckColumn">複数選択</TableCell>
                    <TableCell className="CheckColumn">並替キー</TableCell>
                    <TableCell>設定値</TableCell>
                    <TableCell>Enlightカラム名</TableCell>
                    <TableCell className="SettingButton">変更/削除</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {freeRowsRawAttached.map((row) => (
                    <TableRow
                      key={row.name}
                    >
                      <TableCell component="td" scope="row">
                        {/* 項目名 */}
                        <strong>{row.name}</strong>
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 入力方法 */}
                        {mapAttrTypeToLabel(row.meta?.attr_type, row.type)}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 必須 */}
                        {row.required && (
                          <DoneIcon color="primary"></DoneIcon>
                        )}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 複数選択 */}
                        {row.multiple_select && (
                          <DoneIcon color="primary"></DoneIcon>
                        )}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* 並替キー */}
                        {(() => {
                          const v = row.metaRaw?.use_for_sort ?? (row.meta as any)?.use_for_sort;
                          const enabled = v === true || v === 1 || String(v).toLowerCase() === 'true';
                          return enabled ? <DoneIcon color="primary" /> : null;
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="settings-scroll" style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 8 }}>
                          {/* 設定値（division/team でフィルタ、status='N' は非表示） */}
                          {Array.isArray(row.datas) && filterOptionsByOrg(row.datas as OptionsSelectList[], division, team)
                            .filter((line: OptionsSelectList) => String((line as any).status ?? 'Y').toUpperCase() !== 'N')
                            .map((line: OptionsSelectList) => {
                              const color = line.color;
                              if (color) {
                                return (
                                  <p className="setting-value" key={line.value}>
                                    {line.label}
                                    <input type="color" className="color-setting" value={color} disabled></input>
                                  </p>
                                )
                              } else {
                                return (
                                  <p className="setting-value" key={line.value}>
                                    {line.label}
                                  </p>
                                )
                              }
                            })}
                        </div>
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {/* Enlightカラム名 */}
                        {(() => {
                          const id = Number(row?.setting_id);
                          const c = (teamDocsetColumns ?? []).find((x: any) => Number(x?.attr_id) === id);
                          const name = String(c?.enlight_column_name ?? '').trim();
                          const dtypeRaw = String((c as any)?.enlight_dype ?? (c as any)?.enlight_dtype ?? '').trim();
                          const dtype = normalizeEnlightDtype(dtypeRaw);
                          const expected = computeBodyType(row.meta?.attr_type, row.multiple_select);
                          const mismatch = Boolean(dtype) && Boolean(expected) && dtype !== expected;
                          if (!name) return (<span className="setting-value">—</span>);
                          return (
                            <span className="setting-value">
                              {name}
                              <br />
                              <span style={{ fontSize: '0.75em' }}>
                                {dtype ? `[${dtype}]` : ''}
                                {mismatch && (
                                  <Tooltip title="データ種別が異なるため、内容が正常に反映されないことがございます。">
                                    <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                  </Tooltip>
                                )}
                              </span>
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell component="td" scope="row" align="center">
                        {/* 変更・削除ボタン（縦配置） */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() =>
                              openSettingDialog(
                                row.name,
                                row.metaRaw,
                                filterOptionsByOrg(row.datas, division, team),
                                false,
                                true,
                                row.editColor,
                                team,
                                true,
                                sharedEnlightTeam,
                                teamDocsetColumns,
                                sharedEnlightSubteam,
                                sharedEnlightDocsetId
                              )
                            }
                          >
                            設定変更
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            sx={{ mt: 1 }}
                            onClick={() => handleDeleteFreeAttribute(row?.setting_id, row?.name)}
                            disabled={!canEdit}
                          >
                            削除
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    <ConfirmDialog
      open={deleteDialog.open}
      title="フリー項目の削除"
      description={renderDeleteConfirmDescription(deleteDialog.usingList, deleteDialog.targetName)}
      confirmText="削除する"
      cancelText="キャンセル"
      confirmColor="error"
      confirmVariant="contained"
      cancelVariant="outlined"
      processing={deleteDialog.processing}
      confirmDisabled={!canEdit}
      onConfirm={confirmDeleteFreeAttribute}
      onClose={closeDeleteDialog}
      onCancel={closeDeleteDialog}
    />
    <Dialog
      open={createDocsetErrorDialog.open}
      onClose={() => setCreateDocsetErrorDialog({ open: false, message: '' })}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>エラー</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {createDocsetErrorDialog.message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setCreateDocsetErrorDialog({ open: false, message: '' })}
          variant="outlined"
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  </FormControl>
  );
};

export default ChildConfig;


/**
 * Enlightのdtype文字列を BodyType 比較用に正規化
 * - number系: int/integer/float/double/decimal/number -> 'number'
 * - date系: date/datetime/timestamp -> 'date'
 * - list系: list/array/enum/set/multi/multiselect -> 'list'
 * - その他 -> 'text'
 */
function normalizeEnlightDtype(dtype?: any): string {
  const s = String(dtype ?? '').trim().toLowerCase();
  if (!s) return '';
  if (['int', 'integer', 'float', 'double', 'decimal', 'number'].includes(s)) return 'number';
  if (['date', 'datetime', 'timestamp'].includes(s)) return 'date';
  if (['list', 'array', 'enum', 'set', 'multi', 'multiselect'].includes(s)) return 'list';
  return 'text';
}

/**
 * attr_type表示ラベル変換（連想配列版）
 */
function mapAttrTypeToLabel(attrType?: number | string, fallback?: string): string {
  const n = Number(attrType);
  if (!Number.isFinite(n)) return fallback ?? '';
  return attrTypeLabelMap[n] ?? (fallback ?? '');
}

/**
 * 削除確認ダイアログのdescription生成
 * - usingList が0件: 既存の文言を返す
 * - 1件以上: 指定の文言＋タイトル箇条書きを返す（最大5件表示、超過は「他X件」）
 */
function renderDeleteConfirmDescription(usingList: any[] | undefined | null, targetName: string): React.ReactNode {
  const list = Array.isArray(usingList) ? usingList : [];
  if (list.length === 0) {
    return `”${targetName}”を削除します。よろしいですか？`;
  }

  const maxDisplay = 5;
  const display = list.slice(0, maxDisplay);
  const extra = Math.max(0, list.length - display.length);

  return (
    <span>
      以下の週報で”{targetName}”が使用されています。削除してもよろしいですか？
      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
        {display.map((it: any) => {
          const t = String(it?.title ?? '').trim();
          const key = String(it?.wr_id ?? t);
          return <li key={key}>{t}</li>;
        })}
      </ul>
      {extra > 0 && (
        <div>他{extra}件</div>
      )}
    </span>
  );
}
